import { cn } from '../../lib/cn'
import type { CourseGeometry, CourseHole } from '../../lib/golfRound'
import { holeIn, shotsForHole } from '../../lib/holeProjection'
import { HolePlan } from './HolePlan'
import type { HoleTracePlayer } from './HoleTraceStage'

export interface HoleTraceInlineProps {
  holeNumbers: readonly number[]
  courseHoles: readonly CourseHole[]
  players: readonly HoleTracePlayer[]
  geometry: CourseGeometry | null
  courseName: string
  className?: string
}

/**
 * A THIN STRIP per hole — the compact variant, meant to run down beside the
 * card rather than take a section of its own.
 *
 * Built and tested but NOT wired into the round page: the maintainer chose the
 * stage as the default and asked for all three presentations to exist as real
 * components (decision record §2, 2026-08-22 —
 * `../../../../../../docs/fireside/club-web-decisions.md`).
 *
 * ## Why a strip is per HOLE and not per player
 *
 * The strip is LANDSCAPE — a wide, short box — which is the one aspect ratio
 * that sits inline with a table row without pushing the page around, and every
 * player's walk down the same hole fits in it at once. A strip per player
 * would be a strip per player PER HOLE, which is seventy-two drawings for a
 * fourball and no longer compact by any reading.
 *
 * ## It lists only the holes it can honestly draw
 *
 * A hole with no traced geometry, or with no shots recorded on it, has no
 * strip at all rather than an empty frame with a hole number on it — the row
 * would spend the height and announce itself to a screen reader to say
 * nothing. Where that leaves nothing at all, the component says so in one
 * line. A short list IS the answer: these are the holes the round drew.
 */
export function HoleTraceInline({
  holeNumbers,
  courseHoles,
  players,
  geometry,
  courseName,
  className,
}: HoleTraceInlineProps) {
  const drawn = holeNumbers
    .map((number) => ({
      number,
      geometry: holeIn(geometry, number),
      card: courseHoles.find((courseHole) => courseHole.number === number),
      played: players.some((player) => shotsForHole(player.shots, number).length > 0),
    }))
    .filter((row) => row.geometry !== undefined && row.played)

  if (drawn.length === 0 || geometry === null) {
    return (
      <p className={cn('text-[13px] text-ink-soft', className)} data-inline-empty="">
        {geometry === null
          ? `The club has not mapped ${courseName}. The card is complete; the drawings are not.`
          : 'No hole of this round has a trace to draw.'}
      </p>
    )
  }

  return (
    <ul className={cn('flex flex-col gap-2', className)} data-hole-trace-inline="">
      {drawn.map((row) => (
        <li
          key={row.number}
          data-inline-hole={row.number}
          className="flex items-center gap-3 rounded-club border border-sage-soft bg-parchment px-3 py-2"
        >
          <span className="w-16 shrink-0 text-[12px] tabular-nums text-ink-soft">
            <b className="font-display text-[15px] text-pine">{row.number}</b>
            {row.card && <span className="ml-1.5">par {row.card.par}</span>}
          </span>
          <HolePlan
            hole={row.geometry!}
            scale={geometry.scale}
            par={row.card?.par ?? null}
            players={players}
            // Landscape, and deliberately shallow: the strip states which way
            // the walk went, not how far off line it was.
            width={420}
            height={96}
            className="min-w-0 flex-1"
          />
        </li>
      ))}
    </ul>
  )
}
